import { UserRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, useNavigate } from 'react-router-dom';

import { useIsAdmin, useLogout, useMe } from '../hooks';
import { useTheme } from '../providers/ThemeProvider';
import AlertSoundToggle from './AlertSoundToggle';
import LanguageSwitcher from './LanguageSwitcher';
import { Button, Logo } from './ui';

export default function NavBar() {
  const { theme, setTheme } = useTheme();
  const { data: me } = useMe();
  const { mutate: logout, isPending } = useLogout();
  const isAdminUser = useIsAdmin(me);
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // TODO: need standard for such elements
  const linkBase = 'px-3 py-2 rounded-md text-sm font-medium transition-colors';
  const active = 'bg-accent text-white';
  const inactive = 'text-foreground hover:bg-gray-light';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <nav className="w-full bg-background-light border-b border-border">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-12 items-center gap-2">
          <Logo variant="lockup" size="md" className="mr-4">
            <FormattedMessage id="nav.appName" />
          </Logo>
          <NavLink
            to="/devices"
            className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
          >
            <FormattedMessage id="nav.devices" />
          </NavLink>
          <NavLink
            to="/analytics"
            className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
          >
            <FormattedMessage id="nav.analytics" />
          </NavLink>
          <div className="flex-1" />
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? '🌙' : '☀️'}
          </button>
          <AlertSoundToggle />
          <LanguageSwitcher />
          {me && (
            <div className="relative" ref={menuRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                aria-haspopup="menu"
                data-test-id="user-menu-button"
                aria-expanded={isMenuOpen}
              >
                <span className="font-medium truncate max-w-[140px]">{me.username}</span>
                <UserRound className="h-4 w-4" />
              </Button>

              {isMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-44 rounded-md border border-border bg-background-light shadow-lg py-1 z-50"
                >
                  <NavLink
                    to="/profile"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-background-dark text-foreground'
                          : 'text-foreground hover:bg-background'
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FormattedMessage id="nav.profile" />
                  </NavLink>
                  {isAdminUser && (
                    <NavLink
                      to="/users"
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-background-dark text-foreground'
                            : 'text-foreground hover:bg-background'
                        }`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FormattedMessage id="nav.users" />
                    </NavLink>
                  )}
                  {isAdminUser && (
                    <NavLink
                      to="/firmwares"
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-background-dark text-foreground'
                            : 'text-foreground hover:bg-background'
                        }`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FormattedMessage id="nav.firmwares" />
                    </NavLink>
                  )}
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-background transition-colors"
                    onClick={() => {
                      setIsMenuOpen(false);
                      logout(undefined, {
                        onSuccess: () => navigate('/login', { replace: true }),
                      });
                    }}
                    disabled={isPending}
                  >
                    <FormattedMessage id={isPending ? 'nav.loggingOut' : 'nav.logout'} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
